import { LocalizationPipe } from '@abp/ng.core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Observable, tap } from 'rxjs';
import { FORM_SHARED_IMPORTS } from 'src/app/shared/imports/form-shared.imports';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { callingWindowTimesValidator } from './helpers/calling-window-times.validator';
import { fromTimeOfDayText, toTimeOfDayText } from './helpers/time-of-day.helper';
import { SCHEDULED_CALL_SETTINGS_LIMITS } from './models/scheduled-call-settings-limits.constant';
import { ScheduledCallSettingsDto } from './models/scheduled-call-settings.dto';
import { ScheduledCallSettingsService } from './services/scheduled-call-settings.service';

/** Administration > Settings > "Rescheduled Calls": the scheduled call engine's tenant settings (K-024, 155203). */
@Component({
  selector: 'app-scheduled-call-settings',
  standalone: true,
  imports: [...FORM_SHARED_IMPORTS, LocalizationPipe, DatePickerModule, InputNumberModule, ToggleSwitchModule],
  templateUrl: './scheduled-call-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduledCallSettingsComponent extends BaseFormComponent<ScheduledCallSettingsDto> {
  private readonly settingsService = inject(ScheduledCallSettingsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly limits = SCHEDULED_CALL_SETTINGS_LIMITS;

  isLoading = true;

  /** Last values known to be stored; Cancel restores exactly these. */
  private savedValue: ScheduledCallSettingsDto | null = null;

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  onCancel(): void {
    if (this.savedValue) {
      this.applySettings(this.savedValue);
    }
  }

  protected buildForm(): FormGroup {
    const { minMaxAssignmentAttempts, maxMaxAssignmentAttempts, minReassignmentCooldownMinutes, maxReassignmentCooldownMinutes } =
      this.limits;

    return new FormGroup(
      {
        callingWindowEnabled: new FormControl<boolean>(false, { nonNullable: true }),
        callingWindowStart: new FormControl<Date | null>(null, Validators.required),
        callingWindowEnd: new FormControl<Date | null>(null, Validators.required),
        maxAssignmentAttempts: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(minMaxAssignmentAttempts),
          Validators.max(maxMaxAssignmentAttempts),
        ]),
        reassignmentCooldownMinutes: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(minReassignmentCooldownMinutes),
          Validators.max(maxReassignmentCooldownMinutes),
        ]),
      },
      { validators: callingWindowTimesValidator('callingWindowStart', 'callingWindowEnd') },
    );
  }

  protected override afterFormInit(): void {
    this.settingsService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: settings => {
          this.applySettings(settings);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected override getSubmitValue(): ScheduledCallSettingsDto {
    const value = this.form.getRawValue();

    return {
      callingWindowEnabled: value.callingWindowEnabled,
      callingWindowStart: toTimeOfDayText(value.callingWindowStart),
      callingWindowEnd: toTimeOfDayText(value.callingWindowEnd),
      maxAssignmentAttempts: value.maxAssignmentAttempts,
      reassignmentCooldownMinutes: value.reassignmentCooldownMinutes,
    };
  }

  protected submitRequest(value: ScheduledCallSettingsDto): Observable<ScheduledCallSettingsDto> {
    return this.settingsService.update(value).pipe(tap(accepted => this.applySettings(accepted)));
  }

  /** The global HTTP error handler already shows the localized validation error. */
  protected override onSubmitError(): void {
    return;
  }

  private applySettings(settings: ScheduledCallSettingsDto): void {
    this.savedValue = settings;
    this.form.reset({
      ...settings,
      callingWindowStart: fromTimeOfDayText(settings.callingWindowStart),
      callingWindowEnd: fromTimeOfDayText(settings.callingWindowEnd),
    });
    this.submitted = false;
    this.cdr.markForCheck();
  }
}
